import { useState } from 'react';
import { Plus, RotateCcw, X } from 'lucide-react';
import { MAX_FLOWER_COLORS } from '@peles/shared';
import ColorField, { type ColorSwatch } from '../../common/ColorField';

/**
 * The colours an arrangement mixes, the first the most of. Each is a dot:
 * press one to change it, × to take it out, + to add another (up to six).
 * Left alone, the arrangement takes `inherited` — the pictures', the
 * event's, the default.
 */
export interface FlowerColorsFieldProps {
  value: string[] | undefined;
  inherited: string[];
  inheritedLabel: string;
  onChange: (next: string[] | undefined) => void;
  swatches?: ColorSwatch[];
  /** The field's name — "Flower colours" unless said. */
  label?: string;
}

export function FlowerColorsField({
  value,
  inherited,
  inheritedLabel,
  onChange,
  swatches = [],
  label = 'Flower colours',
}: FlowerColorsFieldProps) {
  const colors = value ?? inherited;
  const [editing, setEditing] = useState(0);
  const at = Math.min(editing, Math.max(colors.length - 1, 0));

  const set = (next: string[]) => onChange(next.length ? next : undefined);
  const change = (i: number, c: string) => set(colors.map((x, k) => (k === i ? c : x)));
  const removeAt = (i: number) => {
    set(colors.filter((_, k) => k !== i));
    setEditing(Math.max(0, i - 1));
  };
  const add = () => {
    const unused = swatches.find((s) => !colors.includes(s.color))?.color ?? '#ffffff';
    set([...colors, unused]);
    setEditing(colors.length);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {value !== undefined && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label={label}>
        {colors.map((c, i) => (
          <span key={`${c}-${i}`} className="relative">
            <button
              type="button"
              onClick={() => setEditing(i)}
              aria-label={`Flower colour ${i + 1}: ${c}`}
              aria-pressed={i === at}
              className={`h-9 w-9 rounded-full border-2 shadow-sm ${
                i === at ? 'border-primary-600 ring-2 ring-primary-200' : 'border-white'
              }`}
              style={{ backgroundColor: c }}
            />
            {colors.length > 1 && (
              <button
                type="button"
                onClick={() => removeAt(i)}
                aria-label={`Remove flower colour ${i + 1}`}
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white text-gray-500 shadow ring-1 ring-gray-200 hover:text-gray-800"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {colors.length < MAX_FLOWER_COLORS && (
          <button
            type="button"
            onClick={add}
            aria-label="Add a flower colour"
            className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>
      {value === undefined && <p className="text-xs text-gray-500">{inheritedLabel}</p>}
      {colors.length > 0 && (
        <ColorField
          label={`Flower colour ${at + 1}`}
          value={colors[at]}
          onChange={(c) => c && change(at, c)}
          swatches={swatches}
          clearable={false}
        />
      )}
    </div>
  );
}
